import React, { useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';

const RazorpayPayment = ({ amount, onSuccess, onFailure, onClose }) => {
  // Parents pass new callback functions on every render; keeping them in a ref stops the
  // effect below from re-running and opening checkout a second time after payment
  const callbacks = useRef({ onSuccess, onClose });
  callbacks.current = { onSuccess, onClose };

  const initializePayment = useCallback(() => {
    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_YOUR_TEST_KEY', // Replace with your actual key
      amount: amount * 100, // Razorpay expects amount in paise
      currency: 'INR',
      name: 'Fijoli Book',
      description: 'Online Book Access',
      image: '/logo192.png',
      handler: function (response) {
        // Payment successful
        callbacks.current.onSuccess(response);
      },
      prefill: {
        name: 'Reader',
        email: 'reader@example.com',
        contact: '9999999999'
      },
      theme: {
        color: '#DC2626'
      },
      modal: {
        ondismiss: function() {
          callbacks.current.onClose();
        }
      }
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  }, [amount]);

  useEffect(() => {
    const loadRazorpay = async () => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      document.body.appendChild(script);

      script.onload = () => {
        initializePayment();
      };
    };

    loadRazorpay();

    return () => {
      const script = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
      if (script) {
        document.body.removeChild(script);
      }
    };
  }, [initializePayment]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
    >
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 text-center">
        <div className="mb-6">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3.012.502A21.736 21.736 0 0012 6.042zM12 6.042A8.967 8.967 0 0018 3.75c1.052 0 2.062.18 3.012.502A21.736 21.736 0 0112 6.042z" />
            </svg>
          </div>
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            Purchase Fijoli Online Access
          </h3>
          <p className="mt-2 text-sm text-gray-500">
            Get unlimited access to read Fijoli online with interactive features.
          </p>
        </div>

        <div className="mb-6">
          <div className="text-3xl font-bold text-gray-900">₹{amount}</div>
          <div className="text-sm text-gray-500">One-time payment</div>
        </div>

        <div className="space-y-3">
          <button
            onClick={initializePayment}
            className="w-full bg-red-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-red-700 transition-colors"
          >
            Pay with Razorpay
          </button>
          <button
            onClick={onClose}
            className="w-full bg-gray-200 text-gray-800 py-3 px-4 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
        </div>

        <div className="mt-4 text-xs text-gray-400">
          Secure payment powered by Razorpay
        </div>
      </div>
    </motion.div>
  );
}

export default RazorpayPayment;
